import { useEffect, useReducer } from 'react';
import { gameReducer, initialState } from '../game/reducer';
import { isMatch } from '../game/rules';
import { PAIRS_PER_DECK, type GameAction, type GameState } from '../game/types';
import { loadApiKey, loadDecks, saveApiKey, saveDecks, clearApiKey } from '../game/storage';
import { decodeDeckFromUrl } from '../game/share';
import { bgm, playSound } from '../services/audio';

export type UseGameReturn = {
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
};

export function useGame(): UseGameReturn {
  const [state, dispatch] = useReducer(gameReducer, undefined, () => ({
    ...initialState,
    savedDecks: loadDecks(),
    customApiKey: loadApiKey(),
  }));

  // 啟動：URL 分享題庫（持久化資料已在 useReducer 初始化時讀入）
  useEffect(() => {
    const shared = decodeDeckFromUrl();
    if (shared) {
      dispatch({ type: 'LOAD_SHARED_DECK', pairs: shared });
    }
  }, []);

  // 翻第二張後判定
  useEffect(() => {
    if (!state.isProcessing || state.flippedIndices.length !== 2) return;
    const [i1, i2] = state.flippedIndices;
    if (i1 === undefined || i2 === undefined) return;
    const isPair = isMatch(state.cards, i1, i2);
    const t = setTimeout(
      () => {
        if (isPair) {
          playSound('match', state.soundEnabled);
          dispatch({ type: 'RESOLVE_MATCH' });
        } else {
          playSound('mismatch', state.soundEnabled);
          dispatch({ type: 'RESOLVE_MISMATCH' });
        }
      },
      isPair ? 800 : 1500,
    );
    return () => clearTimeout(t);
  }, [state.isProcessing, state.flippedIndices, state.cards, state.soundEnabled]);

  // showTurnLayer 1.5s 後關閉
  useEffect(() => {
    if (!state.showTurnLayer) return;
    const t = setTimeout(() => dispatch({ type: 'HIDE_TURN_LAYER' }), 1500);
    return () => clearTimeout(t);
  }, [state.showTurnLayer]);

  // 任一玩家狀態變化後檢查勝負（贏的時候播 win 音效 — 由 phase 變化的下一個 effect 處理）
  useEffect(() => {
    if (state.phase === 'playing') dispatch({ type: 'CHECK_WIN' });
  }, [state.players, state.phase]);

  // 進入 finished 時播 win 音效
  useEffect(() => {
    if (state.phase === 'finished') {
      playSound('win', state.soundEnabled);
    }
  }, [state.phase, state.soundEnabled]);

  // 8 對配完且還沒分出勝負 → 重洗
  useEffect(() => {
    if (state.phase !== 'playing') return;
    if (state.matchedCount < PAIRS_PER_DECK) return;
    const active = state.players.filter((p) => !p.isEliminated);
    if (active.length <= 1) return;
    const t = setTimeout(() => {
      dispatch({ type: 'RESHUFFLE' });
      dispatch({ type: 'SET_NOTIFICATION', message: '🔄 牌用完了，重新洗牌！' });
    }, 500);
    return () => clearTimeout(t);
  }, [state.matchedCount, state.phase, state.players]);

  // 持久化 savedDecks（跳過初始空物件）
  useEffect(() => {
    saveDecks(state.savedDecks);
  }, [state.savedDecks]);

  // 持久化 apiKey
  useEffect(() => {
    if (state.customApiKey) saveApiKey(state.customApiKey);
    else clearApiKey();
  }, [state.customApiKey]);

  // notification 自動消失
  useEffect(() => {
    if (!state.notification) return;
    const t = setTimeout(() => dispatch({ type: 'CLEAR_NOTIFICATION' }), 3000);
    return () => clearTimeout(t);
  }, [state.notification]);

  // BGM 啟停
  useEffect(() => {
    if (state.phase === 'playing' && state.bgmEnabled) bgm.play(true);
    else bgm.stop();
    return () => bgm.stop();
  }, [state.phase, state.bgmEnabled]);

  // aiStatusMessage 3 秒後自動清除（但 AI 思考中時不清，等請求完成）
  useEffect(() => {
    if (!state.aiStatusMessage) return;
    if (state.isGeneratingPairs) return;
    const t = setTimeout(() => dispatch({ type: 'CLEAR_AI_STATUS' }), 3000);
    return () => clearTimeout(t);
  }, [state.aiStatusMessage, state.isGeneratingPairs]);

  return { state, dispatch };
}
